import { t } from "../i18n/index.js";
import type { AuthResult, PlatformAdapter } from "./PlatformAdapter.js";

// v8.0+/Zero-Permissions-only API, not yet in @types/facebook-instant-games
// (that package tops out around SDK v7.1) — see the comment on its usage below.
interface FacebookOverlayViews {
  createAsync(options: { type: "PLAYER_INFO"; domId: string }): Promise<unknown>;
}

// Real Rewarded Video placement ID from Meta Monetisation Manager (property
// "Meksiko", ad space "meksiko_coin_reward" — 2026-09-09, status "Ready to
// publish"). Not the ad space's own name/ID; Facebook's placement IDs are
// namespaced under the app ID.
const REWARDED_PLACEMENT_ID = "1671310101163147_1672995547661269";

// FBInstant.shareAsync requires a base64-encoded preview image — this is a
// 1x1 placeholder pixel; swap for real game art before shipping.
const PLACEHOLDER_SHARE_IMAGE =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=";

/**
 * Facebook Instant Games adapter — Zero Permissions model, SDK v8.0+
 * (GAME_SPEC.md §13 / FACEBOOK_DEPLOYMENT.md). `FBInstant` is injected by
 * the Facebook client at runtime; this file is the only place in the app
 * allowed to touch it.
 */
export class FacebookPlatformAdapter implements PlatformAdapter {
  readonly kind = "facebook" as const;

  private sdk(): typeof FBInstant {
    if (typeof FBInstant === "undefined") {
      throw new Error("FBInstant SDK not present — did you mean to use WebPlatformAdapter?");
    }
    return FBInstant;
  }

  async init(): Promise<void> {
    const fb = this.sdk();
    await fb.initializeAsync();
    fb.setLoadingProgress(100);
    await fb.startGameAsync();
  }

  /** FBInstant's player context is already the identity — no separate login step. */
  async isAuthenticated(): Promise<boolean> {
    return true;
  }

  /** No-op — nothing to pick, see isAuthenticated(). */
  login(): void {}

  /** No-op — see PlatformAdapter.logout(). */
  logout(): void {}

  async authenticate(apiBaseUrl: string): Promise<AuthResult> {
    const fb = this.sdk();
    // getSignedPlayerInfoAsync (not the removed getID()-only trust path) so
    // the server can cryptographically verify this really came from Meta —
    // see apps/server/src/auth/facebook.ts.
    const signedInfo = await fb.player.getSignedPlayerInfoAsync();
    const res = await fetch(`${apiBaseUrl}/api/auth/facebook`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ signedPlayerInfo: signedInfo.getSignature(), referrerUserId: this.readReferrerUserId() }),
    });
    if (!res.ok) throw new Error("Facebook auth failed");
    return res.json();
  }

  /**
   * If this session was launched from another player's shareInvite() (see
   * below), FBInstant.getEntryPointData() carries the inviter's userId back
   * to us — forwarded to /api/auth/facebook so the server can attribute a
   * first-completed-match invite reward to them (GAME_SPEC.md invite
   * referral). Best-effort only: not runtime-verified under the Zero
   * Permissions model (FACEBOOK_DEPLOYMENT.md) — if the API is missing,
   * throws, or returns nothing usable, attribution is silently skipped
   * rather than blocking login.
   */
  private readReferrerUserId(): string | undefined {
    try {
      const data = this.sdk().getEntryPointData() as { inviterId?: unknown } | undefined;
      return typeof data?.inviterId === "string" ? data.inviterId : undefined;
    } catch {
      return undefined;
    }
  }

  async showRewardedAd(): Promise<boolean> {
    const fb = this.sdk();
    try {
      const ad = await fb.getRewardedVideoAsync(REWARDED_PLACEMENT_ID);
      await ad.loadAsync();
      await ad.showAsync();
      return true;
    } catch (err) {
      // FBInstant's APIError.code (ADS_NO_FILL, NETWORK_FAILURE, UNKNOWN, ...)
      // has no distinct "blocked by an ad blocker" value — a blocker most
      // often surfaces as NETWORK_FAILURE or UNKNOWN, indistinguishable here
      // from a genuine no-fill or connectivity issue. Logged (not surfaced to
      // the player) so this is at least visible in support/bug reports.
      console.warn("[Meksiko] showRewardedAd failed", (err as { code?: string })?.code ?? err);
      return false; // ad unavailable, blocked, or the player closed it early
    }
  }

  async shareInvite(inviterId: string): Promise<void> {
    const fb = this.sdk();
    await fb.shareAsync({
      image: PLACEHOLDER_SHARE_IMAGE,
      text: t("share.inviteText"),
      // Read back by the invited friend's own session via
      // FBInstant.getEntryPointData() — see readReferrerUserId() above.
      data: { inviterId },
    });
  }

  renderPlayerPortrait(containerId: string): void {
    const fb = this.sdk() as typeof FBInstant & { overlayViews?: FacebookOverlayViews };
    // Zero Permissions removed direct name/photo APIs — identity must be
    // rendered through FBInstant's own overlay iframe instead of data we
    // fetch ourselves. `overlayViews` isn't in @types/facebook-instant-games
    // yet (see the local FacebookOverlayViews shim below), and its exact call
    // shape is still unconfirmed — reproduced live (2026-09-07) against a
    // real Facebook session: `overlayViews` exists but `.createAsync` is NOT
    // a function on it (`TypeError: ...createAsync is not a function`), and
    // since that's a synchronous call-site error (not a rejected promise), an
    // unguarded call throws before any .catch() ever attaches — it doesn't
    // fail soft, it crashes the caller. try/catch is required here, not just
    // a promise .catch(), until the real method name/shape is confirmed
    // against the current SDK reference. Caller (render.ts) always renders
    // the initials-fallback into this container first, so silently doing
    // nothing here just leaves that fallback showing.
    try {
      const result = fb.overlayViews?.createAsync({ type: "PLAYER_INFO", domId: containerId });
      if (result && typeof result.catch === "function") {
        result.catch(() => {
          /* overlay unsupported/unavailable — fallback stays visible */
        });
      }
    } catch {
      /* overlayViews shape mismatch (e.g. createAsync missing) — fallback stays visible */
    }
  }
}
